"use client";
import {
  FileImageOutlined,
  FilePdfOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import UploadCloud02 from "@untitled-ui/icons-react/build/esm/UploadCloud02";
import {
  Col,
  Form,
  Modal,
  Row,
  Select,
  Space,
  Tooltip,
  Upload,
} from "antd";
import { useState } from "react";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import cnt from "../../../services/definedConstant";
import { BOM } from "../../../services/definedMethod";
import { connect } from "react-redux";

const { Dragger } = Upload;

const OwnershipDetails = (props) => {
  const {
    onFinish,
    form,
    initialValues,
    ownershipTypeOptions,
  } = props;
  const [fileList, setFileList] = useState(initialValues?.documents || []);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewIsPdf, setPreviewIsPdf] = useState(false);

  const isPdfFile = (file) =>
    file?.type === "application/pdf" ||
    file?.name?.toLowerCase().endsWith(".pdf");

  const isImageFile = (file) =>
    file?.type === "image/jpeg" || file?.type === "image/png";

  const handlePreviewFile = async (file) => {
    let previewUrl = "";

    if (file.originFileObj) {
      previewUrl = URL.createObjectURL(file.originFileObj);
    } else if (file.url) {
      previewUrl = file.url;
    }

    setPreviewImage(previewUrl);
    setPreviewTitle(file.name || "Document");
    setPreviewIsPdf(isPdfFile(file));
    setPreviewVisible(true);
  };

  const handleUploadChange = (info) => {
    const newFileList = info.fileList.map((file) => {
      const isPdf = isPdfFile(file);

      return {
        ...file,
        ...(isPdf && { thumbUrl: undefined }),
        isPdf,
      };
    });

    setFileList(newFileList);
  };

  const handleRemoveFile = (removedFile) => {
    setFileList((currentFileList) =>
      currentFileList.filter((file) => file.uid !== removedFile.uid)
    );
    return true;
  };

  const handlePreviewCancel = () => setPreviewVisible(false);

  const uploadProps = {
    listType: "picture",
    maxCount: 10,
    accept: "image/*,.pdf",
    onChange: handleUploadChange,
    onPreview: handlePreviewFile,
    fileList,
    beforeUpload(file) {
      const isJpgOrPng =
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "application/pdf";
      if (!isJpgOrPng) {
        BOM.AlertMsg(cnt.DANGER, "You can only upload JPG/PNG or PDF file!");
        return Upload.LIST_IGNORE;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        BOM.AlertMsg(cnt.DANGER, "File must be smaller than 5MB!");
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    onRemove: handleRemoveFile,
    isImageUrl: (file) => !isPdfFile(file) && isImageFile(file),
    iconRender: (file) =>
      isPdfFile(file) ? (
        <FilePdfOutlined style={{ color: "#D92D20", fontSize: 24 }} />
      ) : (
        <FileImageOutlined style={{ color: "#15CCAE", fontSize: 24 }} />
      ),
  };

  const handleFinish = (data) => {
    onFinish({ ...data, documents: fileList });
  };

  return (
    <div>
      <h1
        style={{
          fontSize: "24px",
          fontWeight: "bold",
          margin: 0,
          marginBottom: "24px",
        }}
      >
        Ownership Details
      </h1>

      <Form
        className="custom-form"
        layout="vertical"
        onFinish={handleFinish}
        form={form}
        initialValues={initialValues}
        style={{ width: "100%" }}
      >
        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Ownership Type
                  <Tooltip title="Select the ownership type">
                    <InfoCircleOutlined
                      style={{
                        marginLeft: "4px",
                        color: "#667085",
                      }}
                    />
                  </Tooltip>
                </span>
              }
              name="ownershipType"
              rules={[
                {
                  required: true,
                  message: "Please select ownership type",
                },
              ]}
            >
              <Select
                placeholder="Select ownership type"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
                options={ownershipTypeOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Document Upload
                  <Tooltip title="Upload supporting documents (Title Deed, Valuation Report, Insurance, etc.)">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
              name="documents"
            >
              <Dragger
                {...uploadProps}
                showUploadList={{
                  showPreviewIcon: true,
                  showRemoveIcon: true,
                }}
                locale={{ uploading: "Uploading..." }}
                style={{
                  border: "2px dashed #15CCAE",
                  borderRadius: "8px",
                  background: "#fafafa",
                }}
              >
                <div style={{ textAlign: "center", padding: "12px 0" }}>
                  <UploadCloud02 color="#15CCAE" />
                  <p
                    style={{
                      margin: "8px 0",
                      fontWeight: 500,
                      color: "#333",
                    }}
                  >
                    <span style={{ color: "#15CCAE" }}>Click to upload</span> or
                    drag and drop
                  </p>
                  <p style={{ fontSize: "12px", color: "#999" }}>
                    PDF, JPG, PNG (max. 5MB each)
                  </p>
                </div>
              </Dragger>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {/* Preview Modal */}
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={handlePreviewCancel}
        width={previewIsPdf ? "80%" : "auto"}
        style={{ top: 20 }}
      >
        {previewIsPdf ? (
          <iframe
            src={`${previewImage}#toolbar=0`}
            style={{ width: "100%", height: "70vh" }}
            title={previewTitle}
          />
        ) : (
          <img
            alt={previewTitle}
            style={{ width: "100%" }}
            src={previewImage || "/placeholder.svg"}
          />
        )}
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(OwnershipDetails);
